import {
  useCallback,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import {
  executionReducer,
  hasActiveExecution,
  type ExecutionEntry,
  type ExecutionTarget,
} from './execution-state';
import { ExecutionTray } from './execution-tray';
import {
  ExecutionContext,
  type StartExecutionOptions,
} from './execution-context';
import { ExecutionAlreadyInProgressDialog } from './execution-already-in-progress-dialog';
import { isAlreadyExecutingError } from './execution-request-error';

/**
 * Creates the local identity used before a GuardianTransaction ID exists,
 * giving every execution target the same client-side state shape.
 */
function createClientExecutionId(): string {
  return globalThis.crypto.randomUUID();
}

/**
 * Registers proposal and transaction executions across route changes, handles
 * already-executing conflicts, and exposes shared lifecycle controls through
 * context while rendering their status tray.
 */
export function ExecutionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [executions, dispatch] = useReducer(executionReducer, []);
  const [alreadyExecutingTarget, setAlreadyExecutingTarget] =
    useState<ExecutionTarget | null>(null);

  /**
   * Removes one independently tracked execution without disturbing other tray
   * entries that may still be running.
   */
  const removeExecution = useCallback((id: string) => {
    dispatch({ type: 'remove', id });
  }, []);

  /**
   * Records completion after terminal callbacks settle so execution lifetime
   * remains inspectable until the card is dismissed.
   */
  const markExecutionCompleted = useCallback((id: string) => {
    dispatch({ type: 'markCompleted', id, completedAt: Date.now() });
  }, []);

  /**
   * Registers the execution immediately. Proposal executions preserve their
   * originating page, while transaction navigation waits for server acceptance
   * so concurrency conflicts leave the current view intact.
   */
  const startExecution = useCallback(
    (options: StartExecutionOptions) => {
      const id = createClientExecutionId();
      const entry: ExecutionEntry = {
        id,
        executionTarget: options.executionTarget,
        executionTargetId: options.executionTargetId ?? null,
        proposalType:
          options.executionTarget === 'proposal' ? options.proposalType : null,
        guardianTransactionId: null,
        immediateError: null,
        createdAt: Date.now(),
        completedAt: null,
        callbacks: {
          onSuccess: options.onSuccess,
          onError: options.onError,
          onComplete: options.onComplete,
        },
      };

      // Register first so the initiating controls block while the request is
      // still waiting for the server to establish execution ownership.
      dispatch({ type: 'add', entry });

      // Resolve the backend request against provider state. Navigation waits
      // for acceptance so a conflicting user stays in their current context.
      // Enter the promise chain before invoking caller code so an unexpected
      // synchronous throw follows the same terminal error path as a rejection.
      void Promise.resolve()
        .then(() => options.request())
        .then((result) => {
          if (!result.guardianTransactionId) {
            throw new Error(
              'Execution request did not return a transaction ID. Please retry.'
            );
          }
          dispatch({
            type: 'setGuardianTransactionId',
            id,
            value: result.guardianTransactionId,
          });
          // Case: accepted transactions continue to their shared status page;
          // proposals stay on the originating page.
          if (options.executionTarget === 'transaction') {
            navigate('/transactions');
          }
        })
        .catch((error: unknown) => {
          // Case: the server accepted another user's execution first. Remove
          // this request's temporary tray entry and surface neutral conflict UI.
          if (isAlreadyExecutingError(error)) {
            dispatch({ type: 'remove', id });
            setAlreadyExecutingTarget(options.executionTarget);

            // Refresh independently from the dialog. The conflict remains the
            // user-facing outcome even if its best-effort synchronization fails.
            const synchronizeServerState = async () => {
              try {
                await options.onAlreadyExecuting?.();
              } catch (refreshError: unknown) {
                console.error(
                  'Failed to refresh after an already-executing response',
                  refreshError
                );
              }
            };
            void synchronizeServerState();
            return;
          }

          // Request failures are terminal entries too, so the tray can explain
          // the failure instead of disappearing.
          const message =
            error instanceof Error
              ? error.message
              : `Failed to execute ${options.executionTarget}. Please try again.`;
          dispatch({
            type: 'setImmediateError',
            id,
            value: message,
            completedAt: Date.now(),
          });
        });

      return id;
    },
    [navigate]
  );

  /**
   * Lets an originating screen block duplicate actions immediately, before
   * its next API poll can observe the GuardianTransaction created by the
   * execute request.
   */
  const isExecutionActive = useCallback(
    (executionTarget: ExecutionTarget, executionTargetId: string) =>
      hasActiveExecution(executions, executionTarget, executionTargetId),
    [executions]
  );

  const value = useMemo(
    () => ({ startExecution, isExecutionActive }),
    [isExecutionActive, startExecution]
  );

  return (
    <ExecutionContext.Provider value={value}>
      {children}
      <ExecutionTray
        executions={executions}
        markExecutionCompleted={markExecutionCompleted}
        removeExecution={removeExecution}
      />
      <ExecutionAlreadyInProgressDialog
        executionTarget={alreadyExecutingTarget}
        onOpenChange={(open) => {
          if (!open) setAlreadyExecutingTarget(null);
        }}
        open={alreadyExecutingTarget !== null}
      />
    </ExecutionContext.Provider>
  );
}
